import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API_URL from '../config/api';
import './Dashboard.css';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState({
    liveSurveys: 0,
    balance: 0,
    earnings: 0
  });
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(false);

  // Calculate cash value from reward points (same logic as Header)
  const calculateBalance = () => {
    const points = stats.balance || 0;
    const country = user?.country || 'US';
    
    // Universal USD standard: 100 points = $0.01 USD (regardless of country)
    const amount = points / 10000; // 10,000 points = $1 USD

    // Display USD with 2 decimal places
    const formatted = amount.toFixed(2);

    return { points, cash: `$${formatted}`, symbol: '$', amount: formatted };
  };

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
    
    // Add body class for dashboard styling
    document.body.classList.add('dashboard-page');
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('dashboard-page');
    };
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch user profile
      const profileResponse = await axios.get(`${API_URL}/users/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check profile completion status
      try {
        const qualificationsResponse = await axios.get(`${API_URL}/qualifications/country-questions`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // If answeredCount is 30 or more, OR if there are no more questions to answer, profile is complete
        const answeredCount = qualificationsResponse.data.answeredCount || 0;
        const totalCount = qualificationsResponse.data.totalCount || 30;
        const remainingQuestions = qualificationsResponse.data.questions?.length || 0;

        // Profile is complete if answered all questions OR no questions remaining
        setProfileComplete(answeredCount >= totalCount || remainingQuestions === 0);
      } catch (err) {
        // Could not fetch profile completion status
      }

      // Fetch available projects/surveys
      let liveSurveys = 0;
      try {
        const projectsResponse = await axios.get(`${API_URL}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        liveSurveys = projectsResponse.data.length || 0;
      } catch (err) {
        // No surveys available yet
      }
      
      setStats({
        liveSurveys,
        balance: profileResponse.data.pointsBalance || 0,
        earnings: profileResponse.data.pointsBalance || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
          {/* Top Section */}
        <div className={`dashboard-top ${profileComplete ? 'stats-only' : ''}`}>
          {/* Profile Section - Only show if profile is not complete */}
          {!profileComplete && (
            <div className="profile-section">
              <div className="profile-card">
                <div className="profile-message">
                  Earn 10 reward points for each profiler question answered.
                </div>
                <div className="profile-content">
                  <div className="profile-text">
                    <h3>Answer questions to get more qualifying surveys.</h3>
                    <Link to="/survey-profiler/all" className="btn-survey-profiler">
                      Complete Your Profile
                    </Link>
                  </div>
                  <div className="profile-illustration">
                    <img src="/assets/images/sync.png" alt="Survey illustration" />
                  </div>
                </div>
              </div>
            </div>
          )}

            {/* Stats Cards */}
            <div className="stats-section">
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-label">LIVE SURVEYS</span>
                </div>
                <div className="stat-value">{stats.liveSurveys}</div>
                <div className="stat-action">
                  <Link to="/projects" className="btn-view">View Surveys</Link>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-label">CURRENT BALANCE</span>
              </div>
              <div className="earnings-display">
                <span className="earnings-text">Earnings - {stats.earnings} pts</span>
                </div>
                <div className="stat-value balance-display">
                <span className="balance-cash">{calculateBalance().cash}</span>
                </div>
                <div className="stat-action">
                  <Link to="/my-rewards" className="btn-view">View Transactions</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Survey History Section */}
          <div className="survey-history">
            <h2 className="section-title">Last 10 Survey History</h2>
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3 className="empty-title">Sorry! No Survey History Found</h3>
              <p className="empty-description">
              Once you start taking surveys, you will be able to see your history here.
              </p>
            </div>
          </div>
        </div>
    </div>
  );
};

export default Dashboard;
